<template>
  <div class="flex h-full flex-col bg-charcoal-950 text-gray-100">
    <core-contextual-error-box v-if="loadError" :action="load" />

    <template v-else>
      <div v-if="shouldShowToolbar" class="shrink-0 border-b border-charcoal-700 bg-charcoal-950 px-3 py-2">
        <div class="flex gap-2">
          <u-button
            icon="i-ri-equalizer-line"
            :variant="areDesignerFiltersApplied ? 'solid' : 'solid'"
            :color="areDesignerFiltersApplied ? 'heroblue' : undefined"
            @click="toggleFilters"
            block
            class="flex-1"
            :ui="areDesignerFiltersApplied ? undefined : {color: {solid: 'shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-charcoal-700 text-gray-900 dark:text-white bg-white hover:bg-gray-50 disabled:bg-white aria-disabled:bg-white dark:bg-charcoal-800 dark:hover:bg-charcoal-700 dark:disabled:bg-charcoal-800 dark:aria-disabled:bg-charcoal-800 focus-visible:ring-2 focus-visible:ring-primary-500 dark:focus-visible:ring-primary-400'}}"
          >Filters <span class="text-xs rounded px-1 py-0.5 leading-none" :class="areDesignerFiltersApplied ? 'bg-white/30' : 'bg-black/10 dark:bg-white/10'" v-if="designerFilterCount">{{designerFilterCount}}</span></u-button>

          <core-responsive-button
            v-if="areDesignerFiltersApplied"
            :icon="COMMON_ICONS.close"
            trailing
            variant="outline"
            color="charcoal"
            @click="clearFilters"
            class="flex-1 dark:text-gray-100 dark:border-charcoal-600"
          >Clear Filters</core-responsive-button>
        </div>
      </div>

      <div class="relative min-h-0 flex-1">
        <div v-show="!showFilterPanel" class="h-full">
          <file-list-grid
            class="pl-3"
            ref="listComp"
            :scrollbar-width="12"
            :scrollbar-width-sm="12"
            :files-store-id="FILES_STORE_ID"
            :files="files || []"
            :disable-approach-bottom="!hasMoreFiles"
            disable-selection
            disable-context-menu
            disable-back-to-top
            :grid-size-id="GRID_SIZE_ID"
            @approach-bottom="getMore"
            @list-item-click="file => addImage(file)"
          >
            <template #list-item-optional-items="{file}">
              <div
                class="absolute-cover draggable-file"
                draggable="true"
                @pointerdown="event => onPointerDown(event, file)"
                @dragstart="event => onDragStart(event, file)"
                @dragend="onDragEnd"
              />
            </template>
          </file-list-grid>

          <div v-if="!hasFiles" class="absolute inset-0 flex justify-center bg-charcoal-950 p-6 text-center">
            <div class="max-w-xs self-start rounded-lg border border-dashed border-charcoal-500 bg-charcoal-800 p-6 shadow-none ring-1 ring-charcoal-600">
              <u-icon name="i-ri-image-line" class="mx-auto mb-3 text-3xl text-gray-200" />
              <div class="text-lg font-semibold text-gray-100">{{emptyState.heading}}</div>
              <div class="mt-2 text-sm text-gray-200">{{emptyState.description}}</div>
            </div>
          </div>
        </div>

        <div v-show="showFilterPanel" class="h-full">
          <file-filter-panel
            :files-store-id="FILES_STORE_ID"
            :allowed-file-types="allowedFileTypes"
            condensed
            hide-clear-filters-action
            hide-header
            default-submit-label="Show All Photos"
            @toggle="toggleFilters"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
  import {storeToRefs} from 'pinia';
  import {makeFilesStore} from '~/stores/files.js';

  const FILES_STORE_ID = 'video-title-frame-designer-files-store';

  const filesStore = makeFilesStore(FILES_STORE_ID)();
  const {files, hasMore, isLoadingFiles, isPageQueued, collectionDescriptor, filesCountFallback} = storeToRefs(filesStore);
  const hasMoreFiles = computed(() => !isPageQueued.value && hasMore.value);

  const {addImage, onPointerDown, onDragStart, onDragEnd} = useSurfaceEditorPhotoActions();

  const GRID_SIZE_ID = 'surface-editor-photos-tab';
  const showFilterPanel = ref(false);

  const grid = useFileListGrid({id: GRID_SIZE_ID});
  grid.setSize(100);

  const hasFiles = computed(() => (files.value?.length || 0) > 0);
  const allowedFileTypes = [FILE_TYPES.photo];
  const allowedFileTypeSet = new Set(allowedFileTypes);

  const activeDesignerFileTypes = computed(() =>
    (collectionDescriptor.value?.fileTypes || []).filter(type => !allowedFileTypeSet.has(type))
  );

  const designerFilterCount = computed(() => {
    let count = 0;

    if (collectionDescriptor.value?.albums?.length) {
      count += collectionDescriptor.value.albums.length;
    }

    if (collectionDescriptor.value?.tags?.length) {
      count += collectionDescriptor.value.tags.length;
    }

    if (collectionDescriptor.value?.people?.length) {
      count += collectionDescriptor.value.people.length;
    }

    count += activeDesignerFileTypes.value.length;

    if (collectionDescriptor.value?.organization?.length) {
      count += collectionDescriptor.value.organization.length;
    }

    if (collectionDescriptor.value?.dateRange?.val) {
      count += 1;
    }

    return count;
  });

  const areDesignerFiltersApplied = computed(() => Boolean(designerFilterCount.value));
  const shouldShowToolbar = computed(() => hasFiles.value || showFilterPanel.value || areDesignerFiltersApplied.value);

  const emptyState = computed(() => areDesignerFiltersApplied.value
    ? {
      heading: 'No matching photos',
      description: 'Adjust or clear your filters to see more photos.'
    }
    : {
      heading: 'No photos yet',
      description: 'Return to All and add photos to use in this design.'
    });

  const loadError = ref(null);
  const getMoreError = ref(null);

  async function load() {
    try {
      await filesStore.getFiles({
        contextType: FILE_CONTEXTS.gallery,
        fileTypes: [FILE_TYPES.photo],
        updateContextFileOrder: false
      });
    } catch (e) {
      loadError.value = e;
    }
  }

  async function getMore() {
    if (!hasMoreFiles.value) {
      return;
    }

    try {
      await filesStore.getFiles({nextPage: true});
    } catch (e) {
      getMoreError.value = e; //todo: what do we do?
    }
  }

  function toggleFilters() {
    showFilterPanel.value = !showFilterPanel.value;
  }

  async function clearFilters() {
    try {
      await filesStore.getFiles({
        ...collectionDescriptor.value,
        albums: [],
        tags: [],
        people: [],
        fileTypes: allowedFileTypes,
        organization: [],
        dateRange: null,
        updateContextFileOrder: false
      });
      showFilterPanel.value = false;
    } catch (e) {
      useErrorToast().add();
    }
  }

  onMounted(() => {
    load();
  });

  onUnmounted(() => {
    filesStore.clearFiles();
  });

  const listComp = ref();

  onActivated(async () => {
    listComp.value?.refreshScrollPosition();
  });

</script>
